// React component for content container with fixed video frames
import React, { useEffect, useRef, useState } from "react";
import sanitize from "sanitize-html"; // Assuming you're using sanitize-html
import ImageModal from "./modal/image-modal";

const ContentContainer = ({ content }) => {
  const contentRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  // Function to wrap videos and iframes after render
  useEffect(() => {
    if (!contentRef.current) return;

    // Find all standalone iframes and videos (not already in video-wrapper)
    const iframes = contentRef.current.querySelectorAll(
      "iframe:not(.video-wrapper iframe)"
    );
    const videos = contentRef.current.querySelectorAll(
      "video:not(.video-wrapper video)"
    );
    const links = contentRef.current.querySelectorAll("a");
    links.forEach((link) => {
      if (!link.hasAttribute("target")) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });
    const addImageClickHandlers = () => {
      const images = contentRef.current.querySelectorAll(
        "img:not(.lesson-thumbnail)"
      );

      images.forEach((image) => {
        // Remove existing listeners to prevent duplicates
        image.removeEventListener("click", imageClickHandler);

        // Add click handler
        image.addEventListener("click", imageClickHandler);
        image.style.cursor = "pointer";
      });
    };

    const imageClickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedImage({
        src: e.target.src,
        alt: e.target.alt || "Image",
      });

      // Manually create and show Bootstrap modal
      const modal = new bootstrap.Modal(document.getElementById("imageModal"));
      modal.show();
    };
    // Function to wrap an element
    const wrapElement = (element) => {
      // Make sure we're not wrapping elements that are already wrapped
      if (
        element.parentElement &&
        element.parentElement.classList.contains("video-wrapper")
      ) {
        return;
      }

      // Create a wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "video-wrapper";

      // Replace the element with the wrapper and append the element to the wrapper
      const parent = element.parentElement;
      if (parent) {
        parent.insertBefore(wrapper, element);
        wrapper.appendChild(element);
      }
    };

    const timer = setTimeout(addImageClickHandlers, 500);

    // Video and iframe handling (existing code)

    // Wrap all standalone elements
    iframes.forEach(wrapElement);
    videos.forEach(wrapElement);

    // Ensure all videos have controls
    videos.forEach((video) => {
      if (!video.hasAttribute("controls")) {
        video.setAttribute("controls", "");
      }
    });
    return () => {
      clearTimeout(timer);
      const images = contentRef.current?.querySelectorAll(
        "img:not(.lesson-thumbnail)"
      );
      images?.forEach((image) => {
        image.removeEventListener("click", imageClickHandler);
      });
    };
  }, [content]); // Re-run when content changes

  return (
    <div
      className="content-container rte-content"
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <div
        ref={contentRef}
        style={{
          width: "100%",
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
        className="m-0 p-0"
        dangerouslySetInnerHTML={{
          __html: sanitize(content, {
            allowedTags: sanitize.defaults.allowedTags.concat([
              "img",
              "iframe",
              "video",
              "source",
              "figure",
              "figcaption",
              "div",
              "a",
            ]),
            allowedAttributes: {
              ...sanitize.defaults.allowedAttributes,
              img: ["src", "alt", "width", "height", "style", "class"],
              a: [
                ...(sanitize.defaults.allowedAttributes.a || []),
                "target",
                "rel",
                "href",
              ],
              iframe: [
                "src",
                "width",
                "height",
                "frameborder",
                "allow",
                "allowfullscreen",
                "style",
                "class",
              ],
              video: [
                "src",
                "controls",
                "width",
                "height",
                "style",
                "class",
                "autoplay",
                "muted",
                "loop",
              ],
              source: ["src", "type"],
              div: ["class", "style"],
              "*": ["class", "style"],
            },
            allowedSchemes: [
              ...sanitize.defaults.allowedSchemes,
              "data",
              "https",
              "youtube",
              "vimeo",
            ],
            allowedStyles: {
              "*": {
                color: [
                  /^#(0x)?[0-9a-f]+$/i,
                  /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
                ],
                "background-color": [
                  /^#(0x)?[0-9a-f]+$/i,
                  /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
                ],
                "text-align": [/^left$/, /^right$/, /^center$/],
                width: [/^\d+(?:px|em|%|vw)$/],
                height: [/^\d+(?:px|em|%|vh)$/],
                "max-width": [/^\d+(?:px|em|%|vw)$/],
                "min-width": [/^\d+(?:px|em|%|vw)$/],
                "max-height": [/^\d+(?:px|em|%|vh)$/],
                "min-height": [/^\d+(?:px|em|%|vh)$/],
                margin: [/^[\d\s\.\-]+(?:px|em|%|rem)$/],
                padding: [/^[\d\s\.\-]+(?:px|em|%|rem)$/],
                position: [/^relative$/, /^absolute$/, /^fixed$/],
                display: [/^block$/, /^inline-block$/, /^flex$/],
                "aspect-ratio": [/^[\d\/\.\:]+$/],
                overflow: [/^hidden$/, /^auto$/, /^visible$/],
                top: [/^[\d\.\-]+(?:px|em|%|vh)$/],
                left: [/^[\d\.\-]+(?:px|em|%|vw)$/],
                bottom: [/^[\d\.\-]+(?:px|em|%|vh)$/],
                right: [/^[\d\.\-]+(?:px|em|%|vw)$/],
              },
            },
          }),
        }}
      />
      {selectedImage && (
        <ImageModal
          selectedImage={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default ContentContainer;
